import React from 'react';
import ProductTable from '../ProductTable';
import HSNSummary from '../HSNSummary';

const ClassicTemplate = ({
    invoiceSummary,
    products,
    sellerDetails,
    buyerDetails,
    amountInWords,
    gstType,
    isInterState,
    formatCurrency,
    invoiceNumber,
    invoiceDate,
    logo,
    shippingAddress,
    isSameAddress,
    showSignature,
    transportDetails
}) => {
    return (
        <div className="bg-white shadow-lg w-[794px] min-h-[1123px] mx-auto animate-in zoom-in-95 duration-200 font-serif text-slate-900">

            <div className="px-6 pt-6 pb-10 border-2 border-slate-800 m-4">

                {/* Header Section */}
                <div className="border-b-2 border-slate-800 pb-2 mb-2 flex justify-between items-center relative">
                    <div className="flex items-center gap-3">
                        {/* Business Logo - Shown Prominently if uploaded */}
                        {logo && (
                            <div className="mt-2">
                                <img
                                    src={logo}
                                    alt="Business Logo"
                                    className="object-contain"
                                    style={{ height: '80px', maxWidth: '150px', width: 'auto' }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="text-right">
                        {/* Clean ERP Logo - Right side above Tax Invoice */}
                        <div className="flex justify-end mb-1 mt-2">
                            <img src="/logo.png" alt="Clean ERP" className="h-5 w-auto object-contain" />
                        </div>
                        <h1 className="text-2xl font-bold uppercase tracking-wide">
                            {gstType === 'Regular GST' ? 'Tax Invoice' :
                                gstType === 'Composition GST' ? 'Bill of Supply' : 'Invoice'}
                        </h1>
                        <div className="text-sm">Original for Recipient</div>
                    </div>
                </div>

                {/* Info Grid */}
                <div className="border-2 border-slate-800 mb-6">
                    <div className="flex">
                        <div className="flex-1 p-4 border-r-2 border-slate-800">
                            <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Invoice No</div>
                            <div className="text-base font-bold">{invoiceNumber || 'INV-001'}</div>
                        </div>
                        <div className="flex-1 p-4">
                            <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Date</div>
                            <div className="text-base font-bold">{new Date(invoiceDate).toLocaleDateString('en-IN')}</div>
                        </div>
                    </div>
                </div>

                {/* Parties Grid - Refactored: Seller Left, Buyer Right */}
                <div className="grid grid-cols-2 gap-0 border-2 border-slate-800 mb-6">
                    {/* Left Column: Seller */}
                    <div className="p-4 border-r-2 border-slate-800">
                        <h3 className="font-bold mb-2 pb-1 uppercase text-xs tracking-wider border-b border-slate-200">Seller Info</h3>
                        <div className="text-lg font-bold">{sellerDetails.name}</div>
                        <div className="text-sm mt-1 whitespace-pre-wrap">{sellerDetails.address}</div>
                        {sellerDetails.state && <div className="text-sm font-bold mt-1">State: {sellerDetails.state}</div>}
                        {sellerDetails.gstNumber && <div className="text-sm">GSTIN: {sellerDetails.gstNumber}</div>}
                    </div>

                    {/* Right Column: Buyer */}
                    <div className="p-4">
                        <h3 className="font-bold mb-2 pb-1 uppercase text-xs tracking-wider border-b border-slate-200">Buyer Info</h3>
                        <div className="text-lg font-bold">{buyerDetails.name}</div>
                        <div className="text-sm mt-1 whitespace-pre-wrap">{buyerDetails.address}</div>
                        {buyerDetails.state && <div className="text-sm font-bold mt-1">State: {buyerDetails.state}</div>}
                        {buyerDetails.gstNumber && buyerDetails.gstNumber !== 'NOT APPLICABLE' && <div className="text-sm">GSTIN: {buyerDetails.gstNumber}</div>}
                    </div>
                </div>

                {/* Addresses Row - Billing Left, Shipping Right */}
                <div className="grid grid-cols-2 gap-0 border-2 border-slate-800 mb-6">
                    {/* Billing Address - Left */}
                    <div className="p-4 border-r-2 border-slate-800">
                        <h3 className="font-bold mb-2 pb-1 uppercase text-xs tracking-wider border-b border-slate-800">Billing Address</h3>
                        <div className="text-sm whitespace-pre-wrap">{buyerDetails.address}</div>
                    </div>
                    {/* Shipping Address - Right */}
                    <div className="p-4">
                        <h3 className="font-bold mb-2 pb-1 uppercase text-xs tracking-wider border-b border-slate-800">Shipping Address</h3>
                        <div className="text-sm whitespace-pre-wrap">{isSameAddress ? buyerDetails.address : shippingAddress}</div>
                    </div>
                </div>

                {/* Transport Details - Matches classic bordered style */}
                {transportDetails && (transportDetails.transporterName || transportDetails.vehicleNo || transportDetails.ewayBillNo) && (
                    <div className="border-2 border-slate-800 mb-6 p-4">
                        <h3 className="font-bold mb-3 pb-1 uppercase text-xs tracking-wider border-b border-slate-800">Transport Details</h3>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                            {transportDetails.transporterName && (
                                <div className="flex justify-between">
                                    <span className="font-bold uppercase text-[10px] text-slate-600">Transporter:</span>
                                    <span className="font-bold tracking-tight">{transportDetails.transporterName}</span>
                                </div>
                            )}
                            {transportDetails.transporterGst && (
                                <div className="flex justify-between">
                                    <span className="font-bold uppercase text-[10px] text-slate-600">GSTIN:</span>
                                    <span className="font-bold tracking-tight">{transportDetails.transporterGst}</span>
                                </div>
                            )}
                            {transportDetails.vehicleNo && (
                                <div className="flex justify-between">
                                    <span className="font-bold uppercase text-[10px] text-slate-600">Vehicle No:</span>
                                    <span className="font-bold tracking-tight">{transportDetails.vehicleNo}</span>
                                </div>
                            )}
                            {transportDetails.ewayBillNo && (
                                <div className="flex justify-between">
                                    <span className="font-bold uppercase text-[10px] text-slate-600">E-Way Bill:</span>
                                    <span className="font-bold tracking-tight">{transportDetails.ewayBillNo}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Product Table - Custom styling for Classic (forcing borders) */}
                <div className="mb-6">
                    <ProductTable
                        products={products}
                        isInterState={isInterState}
                        gstType={gstType}
                        isPreview={true}
                        stateTaxRate={9}
                        variant="classic"
                    />
                    {gstType === 'Regular GST' && (
                        <div className="mt-4">
                            <HSNSummary
                                hsnSummary={invoiceSummary.hsnSummary || []}
                                isBillOfSupply={gstType !== 'Regular GST'}
                                isInterState={isInterState}
                                variant="classic"
                            />
                        </div>
                    )}
                </div>

                {/* Footer / Totals */}
                <div className="border-2 border-slate-800 p-4">
                    <div className="flex justify-between items-start">
                        <div className="w-1/2">
                            <span className="font-bold text-sm block mb-1">Amount In Words:</span>
                            <span className="italic">{amountInWords}</span>
                        </div>

                        <div className="w-1/3 text-right space-y-1 text-sm">
                            <div className="flex justify-between"><span className="font-bold">{gstType !== 'Regular GST' ? 'Sub Total:' : 'Taxable Value:'}</span> <span>{formatCurrency(invoiceSummary.totalTaxable)}</span></div>
                            {gstType === 'Regular GST' && (
                                <div className="flex justify-between"><span className="font-bold">Total Tax:</span> <span>{formatCurrency(invoiceSummary.grandTotal - invoiceSummary.totalTaxable)}</span></div>
                            )}
                            <div className="border-t border-slate-800 my-2"></div>
                            <div className="flex justify-between text-lg font-bold"><span>Grand Total:</span> <span>{formatCurrency(invoiceSummary.grandTotal)}</span></div>
                        </div>
                    </div>
                </div>

                {showSignature && (
                    <div className="mt-2 flex justify-end animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="text-center">
                            <div className="text-xl font-bold font-serif text-slate-900 mb-1">{sellerDetails.name || 'Authorized Signatory'}</div>
                            <div className="h-6 mb-2 border-b border-slate-800 w-48 ml-auto"></div>
                            <div className="font-bold text-xs uppercase pt-1 px-4">Authorized Signatory</div>
                        </div>
                    </div>
                )}

                <div className="mt-8 text-center text-slate-500 text-xs font-sans font-medium">
                    Thank you for your business
                </div>

            </div>
        </div>
    );
};

export default ClassicTemplate;
